/**
 * EventCoreTab — UEIM-04
 * Mounted in OpportunityDetail as the 6th tab "Event Core".
 * Shows: summary card if event master exists, or CTA to create one.
 * Links to the full EventMasterWorkspace at /crm/events/{eid}.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays, MapPin, User, ExternalLink, Plus, AlertCircle,
  CheckCircle2, Clock, Loader2, Zap,
} from "lucide-react";
import { eventApi, commercialApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const STATUS_STYLE = {
  PLANNING:   "bg-amber-100 text-amber-700",
  CONFIRMED:  "bg-indigo-100 text-indigo-700",
  LIVE:       "bg-emerald-100 text-emerald-700",
  COMPLETED:  "bg-slate-100 text-slate-600",
  CLOSED:     "bg-slate-100 text-slate-400",
  CANCELLED:  "bg-red-100 text-red-500",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function EventCoreTab({ oppId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [acceptedRevisions, setAcceptedRevisions] = useState([]);

  const canManage = hasCapability(user, "event.manage");
  const canRead   = hasCapability(user, "event.read");

  const loadEvents = useCallback(async () => {
    if (!canRead) { setLoading(false); return; }
    try {
      const res = await eventApi.listEvents(oppId);
      setEvents(res.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [oppId, canRead]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const loadAcceptedRevisions = async () => {
    try {
      const propsRes = await commercialApi.listProposals(oppId);
      const props = propsRes.data || [];
      const revs = [];
      for (const p of props) {
        const revRes = await commercialApi.listRevisions(p.id);
        (revRes.data || []).filter(r => r.status === "ACCEPTED").forEach(r => {
          revs.push({ ...r, proposal_title: p.title });
        });
      }
      setAcceptedRevisions(revs);
    } catch { /* ignore */ }
  };

  const handleOpenCreate = () => {
    loadAcceptedRevisions();
    setShowCreate(true);
  };

  if (!canRead) {
    return (
      <div className="py-16 text-center text-slate-400">
        <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <p>You do not have access to Event Core for this opportunity.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="py-12 text-center text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        </div>
      ) : events.length === 0 ? (
        <div className="py-20 text-center bg-white rounded-xl border border-slate-200" data-testid="no-event-master">
          <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="font-semibold text-slate-700 text-lg">No Event Master yet</p>
          <p className="text-slate-400 text-sm mt-1 mb-6 max-w-sm mx-auto">
            Create an Event Master from an accepted proposal revision to begin operational planning.
          </p>
          {canManage && (
            <Button onClick={handleOpenCreate} data-testid="create-event-master-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Event Master
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(ev => (
            <div
              key={ev.id}
              className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-sm transition-shadow"
              data-testid={`event-card-${ev.id}`}
            >
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                    <CalendarDays className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{ev.event_name}</h3>
                      <Badge className={`text-xs border-0 ${STATUS_STYLE[ev.status] || "bg-slate-100 text-slate-500"}`}>
                        {ev.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <CalendarDays className="w-3 h-3" />
                        {fmt(ev.event_start_at)} — {fmt(ev.event_end_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {ev.primary_venue}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/crm/events/${ev.id}`)}
                  data-testid={`open-workspace-${ev.id}`}
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open Workspace
                </Button>
              </div>

              {/* Quick stats */}
              <div className="mt-4 grid grid-cols-4 gap-3">
                {[
                  { label: "Functions",  value: ev.functions_count ?? "—" },
                  { label: "Streams",    value: ev.streams_count ?? "—" },
                  { label: "Milestones", value: ev.milestones_count ?? "—" },
                  { label: "Open Issues",value: ev.open_issues_count ?? 0, warn: (ev.open_issues_count ?? 0) > 0 },
                ].map(s => (
                  <div key={s.label} className={`rounded-lg px-3 py-2 text-center ${s.warn ? "bg-red-50 border border-red-100" : "bg-slate-50"}`}>
                    <p className={`text-lg font-bold ${s.warn ? "text-red-600" : "text-slate-800"}`}>{s.value}</p>
                    <p className={`text-xs ${s.warn ? "text-red-500" : "text-slate-400"}`}>{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateEventMasterModal
          oppId={oppId}
          acceptedRevisions={acceptedRevisions}
          onClose={() => setShowCreate(false)}
          onCreated={async (ev) => {
            setShowCreate(false);
            toast.success("Event Master created");
            await loadEvents();
            navigate(`/crm/events/${ev.id}`);
          }}
        />
      )}
    </div>
  );
}

// ── Create Event Master Modal ──────────────────────────────────────────────────
function CreateEventMasterModal({ oppId, acceptedRevisions, onClose, onCreated }) {
  const [source, setSource] = useState(acceptedRevisions.length > 0 ? "ACCEPTED_PROPOSAL" : "LEGACY_CONFIRMED");
  const [form, setForm] = useState({
    accepted_proposal_revision_id: acceptedRevisions[0]?.id || "",
    manual_confirmation_reference: "",
    manual_confirmation_notes: "",
    event_name: "",
    event_start_at: "",
    event_end_at: "",
    primary_venue: "",
    timezone: "Asia/Kolkata",
  });
  const [submitting, setSubmitting] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handle = async () => {
    if (!form.event_name.trim()) { toast.error("Enter an event name"); return; }
    if (!form.event_start_at)    { toast.error("Enter event start date/time"); return; }
    if (!form.event_end_at)      { toast.error("Enter event end date/time"); return; }
    if (!form.primary_venue.trim()) { toast.error("Enter the primary venue"); return; }
    if (source === "ACCEPTED_PROPOSAL" && !form.accepted_proposal_revision_id) {
      toast.error("Select an accepted proposal revision"); return;
    }
    if (source === "LEGACY_CONFIRMED" && !form.manual_confirmation_reference.trim()) {
      toast.error("Enter a manual confirmation reference"); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        opportunity_id: oppId,
        creation_source: source,
        event_name: form.event_name,
        event_start_at: form.event_start_at,
        event_end_at: form.event_end_at,
        primary_venue: form.primary_venue,
        timezone: form.timezone,
        ...(source === "ACCEPTED_PROPOSAL" && { accepted_proposal_revision_id: form.accepted_proposal_revision_id }),
        ...(source === "LEGACY_CONFIRMED" && {
          manual_confirmation_reference: form.manual_confirmation_reference,
          manual_confirmation_notes: form.manual_confirmation_notes || undefined,
        }),
      };
      const res = await eventApi.createEvent(payload);
      onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create Event Master");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 overflow-y-auto max-h-[90vh]" data-testid="create-event-modal">
        <h3 className="font-semibold text-slate-900 text-lg mb-1">Create Event Master</h3>
        <p className="text-sm text-slate-500 mb-5">Set the operational identity of this event.</p>

        {/* Source selection */}
        <div className="mb-4">
          <Label className="text-sm font-medium text-slate-700 mb-2 block">Creation Source</Label>
          <div className="flex gap-2">
            {acceptedRevisions.length > 0 && (
              <button
                onClick={() => setSource("ACCEPTED_PROPOSAL")}
                className={`flex-1 px-4 py-3 rounded-xl border text-sm text-left transition-all ${source === "ACCEPTED_PROPOSAL" ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:border-slate-300"}`}
                data-testid="source-accepted-proposal"
              >
                <p className="font-medium text-slate-800">From Accepted Proposal</p>
                <p className="text-xs text-slate-500 mt-0.5">Scope baseline derived from accepted proposal lines</p>
              </button>
            )}
            <button
              onClick={() => setSource("LEGACY_CONFIRMED")}
              className={`flex-1 px-4 py-3 rounded-xl border text-sm text-left transition-all ${source === "LEGACY_CONFIRMED" ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}
              data-testid="source-legacy-confirmed"
            >
              <p className="font-medium text-slate-800">Manual / Legacy Confirmation</p>
              <p className="text-xs text-slate-500 mt-0.5">Confirmed outside the system — requires explicit evidence</p>
            </button>
          </div>
        </div>

        {/* Source-specific fields */}
        {source === "ACCEPTED_PROPOSAL" && (
          <div className="mb-4">
            <Label className="text-sm text-slate-600 mb-1 block">Accepted Proposal Revision <span className="text-red-400">*</span></Label>
            <select
              value={form.accepted_proposal_revision_id}
              onChange={e => upd("accepted_proposal_revision_id", e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              data-testid="accepted-revision-select"
            >
              <option value="">— Select revision —</option>
              {acceptedRevisions.map(r => (
                <option key={r.id} value={r.id}>
                  {r.proposal_title} — Rev {r.revision_number} ({r.subject})
                </option>
              ))}
            </select>
          </div>
        )}

        {source === "LEGACY_CONFIRMED" && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Confirmation Reference <span className="text-red-400">*</span></Label>
              <Input value={form.manual_confirmation_reference} onChange={e => upd("manual_confirmation_reference", e.target.value)} placeholder="e.g. Email thread ID / PO ref" data-testid="legacy-ref-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Confirmation Notes</Label>
              <Input value={form.manual_confirmation_notes} onChange={e => upd("manual_confirmation_notes", e.target.value)} placeholder="Brief context" />
            </div>
          </div>
        )}

        {/* Event identity */}
        <div className="mb-3">
          <Label className="text-sm font-medium text-slate-700 mb-1 block">Event Name <span className="text-red-400">*</span></Label>
          <Input value={form.event_name} onChange={e => upd("event_name", e.target.value)} placeholder="e.g. Padma Singh — Corporate Conference Dec 2026" data-testid="event-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Start Date & Time <span className="text-red-400">*</span></Label>
            <Input type="datetime-local" value={form.event_start_at} onChange={e => upd("event_start_at", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">End Date & Time <span className="text-red-400">*</span></Label>
            <Input type="datetime-local" value={form.event_end_at} onChange={e => upd("event_end_at", e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Primary Venue <span className="text-red-400">*</span></Label>
            <Input value={form.primary_venue} onChange={e => upd("primary_venue", e.target.value)} placeholder="e.g. The Leela Palace, Bangalore" data-testid="venue-input" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Timezone</Label>
            <Input value={form.timezone} onChange={e => upd("timezone", e.target.value)} />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handle} disabled={submitting} data-testid="create-event-submit">
            {submitting ? "Creating…" : "Create Event Master"}
          </Button>
        </div>
      </div>
    </div>
  );
}
